/**
 * v2Router — the React Router data-router for the v2 (redesign) page tree.
 *
 * Why a data router (`createBrowserRouter`) and not `<BrowserRouter>`:
 * `navigate()` (redesign/nav/useUrl.ts) is a free function called from
 * non-component code, so it needs an imperative handle. `createBrowserRouter`
 * returns a router object with a `.navigate()` method; we register it with the
 * nav layer so the existing `navigate()` / `SpaLink` shims route through it
 * unchanged.
 *
 * The whole point of this tree: providers (Theme + AuthnMe) mount ONCE at the
 * root and the bot-config draft lives in a layout route, so navigating between
 * v2 pages no longer re-inits context or refetches the bot config. Legacy is
 * untouched — App.tsx only mounts <RouterProvider> when `v2Active`.
 *
 * Route parity is kept against utils/urlParams.ts. One intentional, accepted
 * delta: config/operate/call-logs matching tightens from substring to exact
 * path segment (every real URL for those pages is `/b/{id}/<page>`).
 */
import React, { Suspense, lazy, useEffect } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useParams,
  useRouteError,
  type RouteObject,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { ThemeProvider } from '../hooks/useTheme';
import { AuthnMeProvider, useAuthnMe, isAuthnMeConfigured } from '../AuthnMeProvider';
import PreferencesGate from '../PreferencesGate';
import { Toaster } from '../components/ui/sonner';
import HomePageWrapper from '../components/HomePageWrapper';
import LoadingState from '../components/LoadingState';
import { BotConfigProvider } from './data/BotConfigContext';
import ShellLayout from './shell/ShellLayout';
import { registerRouter } from './nav/useUrl';

// v2 pages — same lazy chunks App.tsx uses (rspack dedupes the shared import).
const CustomerV2 = lazy(() => import('./pages/Customer'));
const BotDialV2 = lazy(() => import('./pages/BotDial'));
const BotHeroV2 = lazy(() => import('./pages/BotHero'));
const ConfigureV2 = lazy(() => import('./pages/Configure'));
const TermsV2 = lazy(() => import('./pages/Terms'));
// Standalone tuning bench for the Sidekick spark — no bot/LLM/WS involved.
const SparkLabV2 = lazy(() => import('./pages/SparkLab'));
const PrivacyV2 = lazy(() => import('./pages/Privacy'));
const CallLogsV2 = lazy(() => import('./pages/CallLogs'));
const OperateV2 = lazy(() => import('./pages/Operate'));
const DoNotCallV2 = lazy(() => import('./pages/DoNotCall'));
const OutboundSequenceEditorV2 = lazy(() => import('./pages/OutboundSequenceEditor'));
const BillingUsageV2 = lazy(() => import('./pages/BillingUsage'));
const PackagesV2 = lazy(() => import('./pages/Packages'));
const HelpV2 = lazy(() => import('./pages/Help'));
const ApiDocsV2 = lazy(() => import('./pages/ApiDocs'));
const AdminV2 = lazy(() => import('./pages/Admin'));
const ResellerV2 = lazy(() => import('./pages/Reseller'));
const AdminBillingV2 = lazy(() => import('./pages/AdminBilling'));
const TaskBoardV2 = lazy(() => import('./pages/TaskBoard'));
const WorkflowListV2 = lazy(() => import('./pages/WorkflowList'));
// Schema-bot playground (experimental) — lazy so Monaco stays out of the main bundle.
const SchemaBotV2 = lazy(() => import('./pages/SchemaBot'));

const queryClient = new QueryClient();

function RouteLoadingFallback() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <LoadingState variant="fullPage" message="Loading page..." />
    </div>
  );
}

/** Wrap a lazy page in its own Suspense boundary — matches the per-page
 *  fallback App.tsx gave each route via renderLazyRoute. */
function page(Comp: React.LazyExoticComponent<React.ComponentType<any>>): React.ReactNode {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Comp />
    </Suspense>
  );
}

// Stale/aborted-chunk recovery for route render errors. A lazy route's dynamic
// import fails (stale chunk after a deploy) OR is aborted (user navigates back
// before it finishes) → without an errorElement, React Router shows its raw dark
// "Unexpected Application Error" overlay. This boundary catches those: chunk-family
// errors trigger a throttled reload (lands the user on a fresh page); anything
// else gets a clean, reloadable card instead of the raw overlay.
//
// React.lazy caches the rejected import promise and won't retry on remount, so a
// reload is the only real recovery. The throttle shares index.tsx's
// 'chunk-reload-at' key so the global handler and this boundary can't double-reload.
const CHUNK_RE = /ChunkLoadError|Loading (?:CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i;

function RouteErrorBoundary() {
  const error = useRouteError();
  const msg = String((error as { message?: string } | null)?.message ?? error ?? '');
  const isChunk = CHUNK_RE.test(msg);
  useEffect(() => {
    if (!isChunk) return;
    const last = Number(sessionStorage.getItem('chunk-reload-at') || 0);
    if (Date.now() - last < 10_000) return; // just reloaded — don't loop
    sessionStorage.setItem('chunk-reload-at', String(Date.now()));
    window.location.reload();
  }, [isChunk]);
  if (isChunk) return <RouteLoadingFallback />; // brief fallback while the reload kicks in
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 24, textAlign: 'center' }}>
      <p style={{ font: '500 15px var(--sans, system-ui)' }}>Something went wrong loading this page.</p>
      <button type="button" className="btn brand" onClick={() => window.location.reload()}>Reload</button>
    </div>
  );
}

/** Root element — providers mounted once for the whole v2 session. Privacy/
 *  Terms don't read AuthnMe, but having it present is harmless and keeps the
 *  tree uniform. */
function RootProviders() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthnMeProvider>
          <PreferencesGate>
            <Outlet />
          </PreferencesGate>
        </AuthnMeProvider>
      </QueryClientProvider>
      {/* One app-global toaster for the whole v2 tree (inside ThemeProvider so
          it can theme itself). */}
      <Toaster />
    </ThemeProvider>
  );
}

/** Auth gate as a layout route. Same logic as the old per-call RequireAuth:
 *  waits for isLoading, no-op when AuthnMe isn't configured, and bounces with a
 *  hard `window.location.replace('/')` (full document nav, exactly as before —
 *  NOT a client-side <Navigate>). */
function RequireAuthRoute() {
  const { isAuthenticated, isLoading } = useAuthnMe();
  const enforced = isAuthnMeConfigured();
  useEffect(() => {
    if (enforced && !isLoading && !isAuthenticated) {
      window.location.replace('/');
    }
  }, [enforced, isLoading, isAuthenticated]);
  if (!enforced) return <Outlet />;
  if (isLoading || !isAuthenticated) return <RouteLoadingFallback />;
  return <Outlet />;
}

/** `/b/:botId/conversation` → the embedded workflow tab.
 *
 *  The canvas no longer writes graph edits itself: they're staged as unpublished
 *  drafts in the bot-config provider and written by Configure's "Publish
 *  changes" button. A standalone canvas has no such button, so edits made there
 *  could never be published — hence one editing surface, inside /config. */
function ConversationEditorRedirect() {
  const { botId = '' } = useParams();
  return <Navigate to={`/b/${botId}/config?tab=workflow`} replace />;
}

/** Bot-settings layout — hosts the lazy BotConfigProvider so config + unsaved
 *  edits persist across Config/Operate/CallLogs/Manage. Keyed by botId so
 *  switching bots gets a fresh draft. */
function BotSettingsLayout() {
  const { botId = '' } = useParams();
  return (
    <BotConfigProvider key={botId} botId={botId}>
      <Outlet />
    </BotConfigProvider>
  );
}

/** Home (`/`) — reproduces App.tsx's branch: when AuthnMe is configured, the
 *  HomePageWrapper redirects authed users to their workspace and otherwise
 *  shows the public dial; when not configured, a bare public dial. */
function HomeRoute() {
  // `isAuthnMeConfigured()` reads a module global that is null until
  // /api/auth/config resolves; without an env fallback it is false on the first
  // render, and nothing re-renders this route when the config lands — so a
  // signed-in user stayed on the public dial instead of being sent to their
  // workspace. The context flag is reactive, so subscribe to it as well.
  const { isConfigured } = useAuthnMe();
  if (isConfigured || isAuthnMeConfigured()) {
    return <HomePageWrapper>{page(BotDialV2)}</HomePageWrapper>;
  }
  return <>{page(BotDialV2)}</>;
}

// Customer sub-routes appear with and without a customer-id segment
// (urlParams matches `/customer(/:id)?/<sub>`), so declare both shapes.
const customerSubRoutes: RouteObject[] = [
  { path: 'billing', element: page(BillingUsageV2) },
  { path: 'usage', element: page(BillingUsageV2) }, // v2 folds usage into billing
  { path: 'packages', element: page(PackagesV2) },
  { path: 'tasks', element: page(TaskBoardV2) },
  { path: 'workflows', element: page(WorkflowListV2) },
  { path: 'workflows/new', element: page(WorkflowListV2) },
  { path: 'workflows/:wid/edit', element: page(WorkflowListV2) },
];

export const routes: RouteObject[] = [
  {
    element: <RootProviders />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // Home is always full-bleed (public dial or a redirect wrapper) — it sits
      // outside ShellLayout so it never gets the operator chrome.
      { index: true, element: <HomeRoute /> },

      // Everything else renders inside the persistent ShellLayout. The layout
      // stays mounted across these navigations (so the Sidenav never remounts)
      // and decides per-route whether to draw the shell or a bare Outlet.
      {
        element: <ShellLayout />,
        children: [
          { path: 'privacy', element: page(PrivacyV2) },
          { path: 'terms', element: page(TermsV2) },
          // help / api-docs are open (AuthnMe context present, no login gate).
          { path: 'help', element: page(HelpV2) },
          { path: 'api-docs', element: page(ApiDocsV2) },
          { path: 'spark-lab', element: page(SparkLabV2) },

          // Gated operator surfaces.
          {
            element: <RequireAuthRoute />,
            children: [
              { path: 'admin', element: page(AdminV2) },
              { path: 'admin/billing', element: page(AdminBillingV2) },
              // Path-based admin sections: /admin/<section> and a section sub-view
              // (e.g. a specific plugin: /admin/plugins/pl_x). Static admin/billing
              // above out-ranks these param routes. AdminV2 reads section+sub itself.
              { path: 'admin/:section', element: page(AdminV2) },
              { path: 'admin/:section/:sub', element: page(AdminV2) },
              { path: 'reseller', element: page(ResellerV2) },
              {
                path: 'customer',
                children: [
                  { index: true, element: page(CustomerV2) },
                  ...customerSubRoutes,
                  {
                    path: ':id',
                    children: [
                      { index: true, element: page(CustomerV2) },
                      ...customerSubRoutes,
                    ],
                  },
                ],
              },
            ],
          },

          // Bot namespace. `/b/:botId` is the public dial (bare unless an
          // authenticated operator is viewing it); help/api-docs are open; the
          // rest are gated, and the settings group shares a bot-config draft.
          {
            path: 'b/:botId',
            children: [
              { index: true, element: page(BotDialV2) },
              { path: 'help', element: page(HelpV2) },
              { path: 'api-docs', element: page(ApiDocsV2) },
              {
                element: <RequireAuthRoute />,
                children: [
                  { path: 'conversation', element: <ConversationEditorRedirect /> },
                  { path: 'conversation/canvas', element: <ConversationEditorRedirect /> },
                  {
                    element: <BotSettingsLayout />,
                    children: [
                      { path: 'manage', element: page(BotHeroV2) },
                      { path: 'hero', element: page(BotHeroV2) }, // legacy alias
                      { path: 'config', element: page(ConfigureV2) },
                      { path: 'operate', element: page(OperateV2) },
                      { path: 'operate/do-not-call', element: page(DoNotCallV2) },
                      { path: 'operate/sequences/:sequenceId', element: page(OutboundSequenceEditorV2) },
                      { path: 'call-logs', element: page(CallLogsV2) },
                      // Deep link to a single call. Without this the trailing
                      // {callId} segment matches no child and falls through to the
                      // catch-all (the public dial). CallLogsV2 reads the id from
                      // window.location, so the element is the same as the list.
                      { path: 'call-logs/:callId', element: page(CallLogsV2) },
                      // Schema mode playground (linked from "What it says").
                      { path: 'schema-bot', element: page(SchemaBotV2) },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },

      // Catch-all — mirrors App.tsx's final fall-through to the public dial,
      // so no v2 URL ever renders a blank page. Bare (outside ShellLayout).
      { path: '*', element: page(BotDialV2) },
    ],
  },
];

let _router: ReturnType<typeof createBrowserRouter> | null = null;

/**
 * Lazily create (once) and return the v2 router. Lazy so construction happens
 * during App render — AFTER App.tsx's module-load `consumeUIQueryParam()` has
 * stripped `?ui=` from the URL — and the router snapshots a clean location.
 */
export function getV2Router() {
  if (!_router) {
    _router = createBrowserRouter(routes);
    registerRouter(_router);
  }
  return _router;
}
